/**
 * Feature entitlements for the organization.
 * Each flag indicates whether the organization has access to a specific feature
 * based on their active subscriptions.
 */
export type FeatureFlags = {
  /** Upload & batch transcription flows, session creation for batch mode */
  batch: boolean;
  /** Real-time / live transcription button, realtime session UI */
  realtime: boolean;
  /** Alignment upload option, align-mode session creation */
  alignment: boolean;
  /** Translation controls on documents, language picker for translation */
  translation: boolean;
  /** Custom model training page, "Train model" buttons, model management */
  automatic_training: boolean;
  /** Export template management page, template picker in document export, AI summary */
  templates_export: boolean;
  /** AI summary modal in transcript and subtitle editors */
  ai_summaries: boolean;
  /** Burn-in caption export (video re-encode) in the caption editor */
  burnt_export: boolean;
  /** Publishing connections (YouTube, iBabs, ...) and send-to-platform actions */
  publishing: boolean;
};

/**
 * Known plan SKUs for pricing tiers.
 */
export type PlanSku =
  | 'STARTER:2024'
  | 'PROFESSIONAL:2024'
  | 'BUSINESS:2024'
  | 'ENTERPRISE:2024'
  | 'SMALL:2024'
  | 'MEDIUM:2024'
  | 'LARGE:2024'
  | 'TRIAL:2024'
  | string; // Allow for future SKUs

/**
 * Top-tier SKUs that should hide upgrade buttons.
 */
export const TOP_TIER_SKUS: PlanSku[] = ['BUSINESS:2024', 'ENTERPRISE:2024'];

/**
 * Entitlements response from GET /api/v3/account/entitlements
 */
export type EntitlementsResponse = {
  count: number;
  total_results: number;
  result: Entitlements;
};

/**
 * Organization AI summary allowance, returned at bootstrap.
 * Read from a job response alone, the editors cannot show the allowance at
 * the confirmation step -- the moment a user decides whether to spend one.
 */
export type AiSummaryQuota = {
  enabled: boolean;
  /** Null means unlimited, which is distinct from 0 (exhausted). */
  limit: number | null;
  used: number;
  reserved: number;
  /** Null means unlimited. */
  remaining: number | null;
  /** ISO date the allowance resets, from the billing period. */
  reset_at: string | null;
};

/**
 * Organization entitlements containing feature flags and plan information.
 */
export type Entitlements = {
  /** Feature availability flags */
  features: FeatureFlags;
  /** List of SKU strings for active subscriptions */
  active_plans: PlanSku[];
  /** True when all subscriptions are trial plans */
  is_trial: boolean;
  /** Backend-normalized flag: hide Subscribe/Upgrade panel for partner/custom/top-tier plans */
  hide_upsell?: boolean;
  /** Optional plan-level cap on concurrent realtime streams. Null = no cap. */
  realtime_max_concurrent_streams?: number | null;
  /** Optional plan-level allow-list of realtime language codes. Null/empty = all. */
  realtime_languages?: string[] | null;
  /**
   * Organization may not start new credit-consuming work (upload, realtime,
   * alignment, model training). Reading, editing and exporting stay available.
   * Advisory only — the API enforces this independently.
   */
  upload_suspended?: boolean;
  /** Absent when the backend could not resolve it; treat as unknown, not zero. */
  ai_summary_quota?: AiSummaryQuota | null;
};

/**
 * Conservative default entitlements — all features disabled.
 * Used when the API hasn't responded yet or returns no data.
 * This prevents ungated access when entitlements are unknown.
 */
export const DEFAULT_ENTITLEMENTS: Entitlements = {
  features: {
    batch: false,
    realtime: false,
    alignment: false,
    translation: false,
    automatic_training: false,
    templates_export: false,
    ai_summaries: false,
    burnt_export: false,
    publishing: false
  },
  active_plans: [],
  is_trial: false,
  realtime_max_concurrent_streams: null,
  realtime_languages: null,
  upload_suspended: false
};

/**
 * Permissive fallback entitlements — all features enabled.
 * Used only on transient API errors (5xx, network) so we don't
 * block the entire app for a temporary outage.
 */
export const ERROR_FALLBACK_ENTITLEMENTS: Entitlements = {
  features: {
    batch: true,
    realtime: true,
    alignment: true,
    translation: true,
    automatic_training: true,
    templates_export: true,
    ai_summaries: true,
    burnt_export: true,
    publishing: true
  },
  active_plans: [],
  is_trial: false,
  realtime_max_concurrent_streams: null,
  realtime_languages: null,
  // Fail open: a transient entitlements outage must not look like a suspension.
  // The API rejects uploads independently, so nothing escapes the block.
  upload_suspended: false
};

/**
 * Helper to check if organization has a top-tier plan.
 */
export const hasTopTierPlan = (activePlans: PlanSku[]): boolean => {
  return activePlans.some(sku => TOP_TIER_SKUS.includes(sku));
};

export type UpgradeButtonState = 'hidden' | 'subscribe' | 'upgrade';

export const getUpgradeButtonState = (
  activePlans: PlanSku[],
  hideUpsell?: boolean
): UpgradeButtonState => {
  if (hideUpsell) return 'hidden';
  if (hasTopTierPlan(activePlans)) return 'hidden';
  if (activePlans.length === 0) return 'subscribe';
  return 'upgrade';
};
